import { Fragment, useMemo, useState } from 'react'
import {
  Alert, Autocomplete, Box, Button, IconButton, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material'
import { Check, Pencil, Plus, X } from 'lucide-react'
import { Surface, tokens } from '@agribot/polygon-checks-ui'
import { useDemand, useRemoveDemand, useSetDemand, useTaxonomy, type ApiIngredient, type DemandLine, type DemandSubtotal } from '../api'

/** The bar's layers, lightest to darkest: assigned, traced to origins, approved. */
const LAYER = { assigned: '#bcd9d0', traced: '#6fae9e', approved: tokens.color.brandTeal }
const t = (n: number) => `${n.toLocaleString('en-GB', { maximumFractionDigits: 1 })} t`
const parseTonnes = (v: string) => Number(v.replace(/[\s,]/g, ''))
const tonnesOk = (v: string) => v.trim() !== '' && Number.isFinite(parseTonnes(v)) && parseTonnes(v) > 0

/** Assigned against the need, with the approved part inside it. Wider than the need when over-assigned. */
function CoverageBar({ line }: { line: DemandLine }) {
  if (line.demandT === null) return <Box component="span" sx={{ color: 'text.disabled', fontSize: 12 }}>no need set</Box>
  const scale = Math.max(line.demandT, line.assignedT) || 1
  const pct = (n: number) => `${Math.min(100, (n / scale) * 100)}%`
  const short = (line.gapT ?? 0) > 0
  return (
    <Box sx={{ minWidth: 160 }}>
      <Box sx={{ position: 'relative', height: 8, borderRadius: 999, backgroundColor: tokens.color.ink100, overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', inset: 0, width: pct(line.assignedT), backgroundColor: LAYER.assigned }} />
        <Box sx={{ position: 'absolute', inset: 0, width: pct(line.tracedT), backgroundColor: LAYER.traced }} />
        <Box sx={{ position: 'absolute', inset: 0, width: pct(line.approvedT), backgroundColor: LAYER.approved }} />
        {line.assignedT > line.demandT && <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: pct(line.demandT), width: 2, backgroundColor: tokens.color.ink700 }} />}
      </Box>
      <Box sx={{ mt: 0.75, fontSize: 12, color: short ? tokens.color.warning : 'text.secondary', fontWeight: short ? 500 : 400 }}>
        {line.coveragePct?.toLocaleString('en-GB', { maximumFractionDigits: 1 })}% assigned{short ? ` · ${t(line.gapT!)} short` : ''}
      </Box>
    </Box>
  )
}

function NeedCell({ campaignId, line, editable }: { campaignId: string; line: DemandLine; editable: boolean }) {
  const set = useSetDemand(campaignId)
  const remove = useRemoveDemand(campaignId)
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState('')
  if (editing) {
    return (
      <Box component="form" sx={{ display: 'flex', alignItems: 'center', gap: 1 }} onSubmit={(e) => {
        e.preventDefault()
        if (tonnesOk(value)) set.mutate({ ingredientId: line.ingredientId, demandT: parseTonnes(value) }, { onSuccess: () => setEditing(false) })
      }}>
        <TextField size="small" value={value} onChange={(e) => setValue(e.target.value)} autoFocus inputProps={{ inputMode: 'decimal', 'aria-label': `Tonnes of ${line.name} needed` }} sx={{ width: 110 }} />
        <IconButton type="submit" aria-label="Save" disabled={!tonnesOk(value) || set.isPending} size="small"><Check size={16} /></IconButton>
        <IconButton aria-label="Cancel" onClick={() => setEditing(false)} size="small"><X size={16} /></IconButton>
      </Box>
    )
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box component="span" className="tnum" sx={{ fontWeight: 500, color: line.demandT === null ? 'text.disabled' : 'text.primary' }}>
        {line.demandT === null ? '—' : t(line.demandT)}
      </Box>
      {editable && (
        <Tooltip title={line.demandT === null ? 'Set the need' : 'Change the need'}>
          <IconButton size="small" aria-label={`${line.demandT === null ? 'Set' : 'Change'} the need for ${line.name}`}
            onClick={() => { setValue(line.demandT === null ? '' : String(line.demandT)); setEditing(true) }} sx={{ color: tokens.color.ink500 }}>
            <Pencil size={14} strokeWidth={1.75} />
          </IconButton>
        </Tooltip>
      )}
      {editable && line.demandT !== null && (
        <Tooltip title="Remove the need">
          <IconButton size="small" aria-label={`Remove the need for ${line.name}`} disabled={remove.isPending}
            onClick={() => remove.mutate(line.ingredientId)} sx={{ color: tokens.color.ink500, '&:hover': { color: tokens.color.danger } }}>
            <X size={14} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  )
}

/** A commodity's row: its ingredients added up, and as tonnes of crop where the taxonomy can say. */
function SubtotalRow({ c, compact }: { c: DemandSubtotal; compact?: boolean }) {
  const crop = [
    c.cropDemandT !== null ? `${t(c.cropDemandT)} of crop needed` : null,
    c.cropAssignedT !== null ? `${t(c.cropAssignedT)} assigned` : null,
  ].filter(Boolean).join(' · ')
  return (
    <TableRow sx={{ backgroundColor: tokens.color.ink50, '& td': { py: 1.5, borderBottom: 0 } }}>
      <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>{c.name}</TableCell>
      <TableCell className="tnum" sx={{ fontWeight: 500 }}>{c.demandT === null ? '' : t(c.demandT)}</TableCell>
      <TableCell align="right" className="tnum" sx={{ fontWeight: 500 }}>{t(c.assignedT)}</TableCell>
      {!compact && <TableCell align="right" className="tnum">{t(c.tracedT)}</TableCell>}
      {!compact && <TableCell />}
      <TableCell align="right" className="tnum">{t(c.approvedT)}</TableCell>
      <TableCell align="right" className="tnum" sx={{ color: c.failingT ? tokens.color.danger : undefined }}>{t(c.failingT)}</TableCell>
      <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
        {crop && <Box>{crop}</Box>}
        {c.notBrokenDownT > 0 && <Box sx={{ color: tokens.color.warning }}>{t(c.notBrokenDownT)} with no ingredient yet</Box>}
      </TableCell>
    </TableRow>
  )
}

/**
 * How many tonnes of each ingredient the campaign needs, and how much of it
 * is assigned to suppliers, traced to origins, submitted and approved
 * (GET /campaigns/{id}/demand-targets), grouped under each commodity with
 * its subtotal. Administrators set the needs here while the campaign is not
 * closed; elsewhere it reads only.
 */
export function DemandPanel({ campaignId, editable, compact }: { campaignId: string; editable: boolean; compact?: boolean }) {
  const { data, isPending, error } = useDemand(campaignId)
  const { data: taxonomy } = useTaxonomy()
  const set = useSetDemand(campaignId)
  const [adding, setAdding] = useState(false)
  const [ingredient, setIngredient] = useState<ApiIngredient | null>(null)
  const [tonnes, setTonnes] = useState('')
  const commodityName = useMemo(() => new Map((taxonomy?.commodities ?? []).map((c) => [c.id, c.name])), [taxonomy])
  const listed = new Set(data?.items.filter((i) => i.demandT !== null).map((i) => i.ingredientId))
  const addable = (taxonomy?.ingredients ?? [])
    .filter((f) => f.active && !listed.has(f.id))
    .sort((a, b) => (commodityName.get(a.commodityId) ?? '').localeCompare(commodityName.get(b.commodityId) ?? '') || a.name.localeCompare(b.name))
  const lines = data?.items ?? []
  const groups = (data?.commodities ?? []).map((c) => ({ c, lines: lines.filter((l) => l.commodityId === c.commodityId) }))
  const columns = compact ? 6 : 8

  return (
    <Surface component="section" aria-label="Volume against need" sx={{ p: { xs: 4, md: 5 } }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 3, flexWrap: 'wrap', mb: 1 }}>
        <Typography variant="h5" component="h2" sx={{ flex: 1 }}>Volume against need</Typography>
        {editable && !adding && addable.length > 0 && (
          <Button variant="text" size="small" startIcon={<Plus size={14} />} onClick={() => { setAdding(true); setIngredient(null); setTonnes('') }}>
            Set a need
          </Button>
        )}
      </Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4, maxWidth: 760 }}>
        How many tonnes of each ingredient this campaign needs, and how much of it is assigned to suppliers, traced to origins
        and approved so far, in tonnes as bought. Each commodity adds up its ingredients, and gives them as tonnes of harvested
        crop where the taxonomy has their conversion rates.
      </Typography>
      {error && <Alert severity="error">{(error as Error).message}</Alert>}
      {(set.isError) && <Alert severity="error" sx={{ mb: 3 }}>{(set.error as Error).message}</Alert>}
      {adding && (
        <Box component="form" sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 4, flexWrap: 'wrap' }} onSubmit={(e) => {
          e.preventDefault()
          if (ingredient && tonnesOk(tonnes)) set.mutate({ ingredientId: ingredient.id, demandT: parseTonnes(tonnes) }, { onSuccess: () => setAdding(false) })
        }}>
          <Autocomplete
            size="small" options={addable} value={ingredient} onChange={(_, v) => setIngredient(v)} sx={{ minWidth: 280 }}
            groupBy={(o) => commodityName.get(o.commodityId) ?? ''} getOptionLabel={(o) => o.name} isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(p) => <TextField {...p} label="Ingredient" autoFocus InputLabelProps={{ ...p.InputLabelProps, shrink: true }} />}
          />
          <TextField size="small" label="Tonnes needed" value={tonnes} onChange={(e) => setTonnes(e.target.value)}
            inputProps={{ inputMode: 'decimal' }} sx={{ width: 150 }} InputLabelProps={{ shrink: true }} />
          <Button type="submit" size="small" disabled={!ingredient || !tonnesOk(tonnes) || set.isPending}>Save</Button>
          <Button variant="text" size="small" onClick={() => setAdding(false)}>Cancel</Button>
        </Box>
      )}
      {isPending && <Typography variant="body2" sx={{ color: 'text.secondary' }}>Loading…</Typography>}
      {data && groups.length === 0 && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Nothing assigned yet{editable ? ', and no need set: Set a need to compare the campaign against it.' : '.'}
        </Typography>
      )}
      {groups.length > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: compact ? 640 : 900, '& td, & th': { px: 2 } }}>
            <TableHead>
              <TableRow>
                <TableCell>Ingredient</TableCell>
                <TableCell>Need</TableCell>
                <TableCell align="right">Assigned</TableCell>
                {!compact && <TableCell align="right">Traced to origins</TableCell>}
                {!compact && <TableCell align="right">Submitted</TableCell>}
                <TableCell align="right">Approved</TableCell>
                <TableCell align="right">On failing origins</TableCell>
                <TableCell>Coverage</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map(({ c, lines: mine }) => (
                <Fragment key={c.commodityId}>
                  <SubtotalRow c={c} compact={compact} />
                  {mine.map((l) => (
                    <TableRow key={l.ingredientId} sx={{ '& td': { py: 2, verticalAlign: 'top' } }}>
                      <TableCell sx={{ pl: '32px !important', fontWeight: 500, color: 'text.primary' }}>{l.name}</TableCell>
                      <TableCell><NeedCell campaignId={campaignId} line={l} editable={editable} /></TableCell>
                      <TableCell align="right" className="tnum">{t(l.assignedT)}</TableCell>
                      {!compact && <TableCell align="right" className="tnum">{t(l.tracedT)}</TableCell>}
                      {!compact && <TableCell align="right" className="tnum">{t(l.submittedT)}</TableCell>}
                      <TableCell align="right" className="tnum">{t(l.approvedT)}</TableCell>
                      <TableCell align="right" className="tnum" sx={{ color: l.failingT ? tokens.color.danger : undefined, fontWeight: l.failingT ? 500 : undefined }}>{t(l.failingT)}</TableCell>
                      <TableCell><CoverageBar line={l} /></TableCell>
                    </TableRow>
                  ))}
                  {mine.length === 0 && (
                    <TableRow><TableCell colSpan={columns} sx={{ pl: '32px !important', fontSize: 12, color: 'text.secondary' }}>
                      No ingredient yet: give its fixed materials their ingredient with Change, or wait for the suppliers’ answers.
                    </TableCell></TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      {data && data.blendsUncomposedT > 0 && (
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 3 }}>
          {t(data.blendsUncomposedT)} of blends is not broken down by its supplier yet, so it is not counted under any ingredient.
        </Typography>
      )}
      {data && data.commodities.some((c) => c.notBrokenDownT > 0) && (
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
          Fixed materials with no ingredient count under their commodity only, until you give them one (Change on the supplier’s
          row) or the supplier answers.
        </Typography>
      )}
      {lines.length > 0 && (
        <Box sx={{ display: 'flex', gap: 4, mt: 3, fontSize: 12, color: 'text.secondary', flexWrap: 'wrap' }}>
          {[['Assigned', LAYER.assigned], ['Traced to origins', LAYER.traced], ['Approved', LAYER.approved]].map(([label, color]) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: 2, backgroundColor: color }} />{label}
            </Box>
          ))}
        </Box>
      )}
    </Surface>
  )
}
